import React, { useMemo, useCallback, memo, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectLending } from '../../selectors';
import { getLendsApi, getLendsInProgress } from '../../actions/getLends';
import { updateUnreadNotifsApi } from '../../actions/updateUnreadNotifs';
import { useIsLoading, useUnreadNotifs } from '../../hooks';
import LendsList from '../../components/LendsList';
import Empty from '../../components/Empty';
import { sortByDate } from '../../utils';
import AppSettingsContext from '../../context/AppSettingsContext';

const tBase = 'screens.lending';

function LendingScreen({ navigation }) {
    const {t} = useContext(AppSettingsContext);
    const dispatch = useDispatch();
    const { unreadLendReturnsIndicatedByBorrower, unreadConsiergeBorrows } = useUnreadNotifs();
    const lending = useSelector(selectLending);
    const isGettingLends = useIsLoading(getLendsInProgress);
    const data = useMemo(() => sortByDate(lending, 'created'), [lending])

    const renderEmpty = useMemo(() => (
        <Empty 
            title={t(`${tBase}.empty.title`)} 
            body={t(`${tBase}.empty.body`)}
            img={ require('../../assets/noLending.png') }
            primaryActionLabel={t(`${tBase}.empty.goToLibrary`)}
            primaryActionOnPress={() => navigation.navigate('LibraryTab') } />      
    ), []);

    const getLends = (checkCaching=true, callback=() => {}) => {
        !isGettingLends && dispatch(getLendsApi({ showLoading: false, checkCaching })).then(callback);
    }

    const onRefresh = useCallback(() => getLends(false), []);

    useEffect(() => {
        const unsubscribe = navigation.addListener('focus', handleOnFocus);
        return unsubscribe;
    }, [unreadLendReturnsIndicatedByBorrower, unreadConsiergeBorrows]);

    const handleOnFocus = () => {
        const shouldCheckCache = (() => {
            const lendingIds = lending.map(({ id }) => id);
            if(unreadLendReturnsIndicatedByBorrower.length) {
                for(let lendReturnIndicatedByBorrowerId of unreadLendReturnsIndicatedByBorrower) {
                    const lend = lending.find(({ id }) => id === lendReturnIndicatedByBorrowerId);
                    if(!lend || lend?.returnIndicatedByBorrowerDatetime === null) {
                         return false;
                    }
                }
            }
            if(unreadConsiergeBorrows.length) {
                for(let unreadConsiergeBorrow of unreadConsiergeBorrows) {
                    if(!lendingIds.includes(unreadConsiergeBorrow)) return false;
                }
            }
            return true;
        })();
        const updateUnreadNotifs = () => {
            if(unreadLendReturnsIndicatedByBorrower.length) {
                dispatch(updateUnreadNotifsApi({ notifsRead: { lendReturnsIndicatedByBorrower: unreadLendReturnsIndicatedByBorrower } }))
            }
            if(unreadConsiergeBorrows.length || unreadLendReturnsIndicatedByBorrower.length) {
                const notifsRead = {};
                if(unreadConsiergeBorrows.length) notifsRead.consiergeBorrows = unreadConsiergeBorrows;
                if(unreadLendReturnsIndicatedByBorrower.length) notifsRead.lendReturnsIndicatedByBorrower = unreadLendReturnsIndicatedByBorrower;
                dispatch(updateUnreadNotifsApi({ notifsRead }))
            }
        }
        getLends(shouldCheckCache, updateUnreadNotifs);
    }
    return (
        <LendsList 
            data={data}
            navigation={navigation}
            onRefresh={onRefresh}
            refreshing={isGettingLends}
            renderEmpty={renderEmpty}
        />
    )
}

export default memo(LendingScreen)